"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { ArrowLeft, ArrowUp, CheckCircle, Wallet, Shield, Clock, TrendingDown, Zap, Info, AlertTriangle, Loader2, User, TrendingUp } from "lucide-react"
import { EmailVerificationScreen } from "./email-verification-screen"
import { LanguageSelector } from "./language-selector"
import { CasinoPinConfirmation } from "./pin-confirmation"
import { useLanguage } from "@/contexts/language-context"
import { useWithdrawalConfig } from "@/hooks/use-withdrawal-config"
import { getExchangeRate, getCurrencySymbol } from "@/lib/services/exchange-rate"
import type { NBCUser } from "@/types/nbc-types"
import { useEffect, useMemo } from "react"

type Step = "email-verification" | "amount" | "pin" | "processing" | "success"

interface MobileWithdrawFlowProps {
  onComplete: (amount: number) => void
  onBack: () => void
  userBalance: number
  prefilledAmount?: number  // Si viene > 0, skip amount step
  orderId?: string  // Para actualizar la orden con el amount seleccionado
  verifiedEmail?: string  // Email verificado del usuario
  nbcUser?: NBCUser | null  // Usuario NBC completo con wallet
  orderData?: any  // Datos completos de la orden con límites dinámicos
}

export function MobileWithdrawFlow({ onComplete, onBack, userBalance, prefilledAmount = 0, orderId, verifiedEmail: propVerifiedEmail = "", nbcUser = null, orderData = null }: MobileWithdrawFlowProps) {
  const { t } = useLanguage()
  // Si prefilledAmount > 0, empezar en PIN, sino en amount
  const initialStep: Step = prefilledAmount > 0 ? "pin" : "amount"
  const [currentStep, setCurrentStep] = useState<Step>(initialStep)
  const [isEmailVerified, setIsEmailVerified] = useState(!!propVerifiedEmail)
  const [verifiedEmail, setVerifiedEmail] = useState(propVerifiedEmail)
  const [amount, setAmount] = useState(prefilledAmount > 0 ? prefilledAmount.toString() : "")
  const [displayCurrency, setDisplayCurrency] = useState<string>("EUR") // Moneda del partner
  const [selectedCurrency, setSelectedCurrency] = useState<"USD" | string>("USD") // USD=USDT o displayCurrency
  const [pin, setPin] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [exchangeRate, setExchangeRate] = useState<number | null>(null) // Se inicializa dinámicamente con la API
  const [withdrawalError, setWithdrawalError] = useState<string | null>(null)
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null)
  const [sessionToken, setSessionToken] = useState<string | null>(null) // Token de sesión del PIN

  // Cargar display_currency del partner y obtener tasa inicial
  useEffect(() => {
    const initializeCurrency = async () => {
      // 1. Establecer displayCurrency del partner
      const currency = orderData?.display_currency || "EUR";
      setDisplayCurrency(currency);
      console.log("💱 Display currency from partner:", currency);
      
      // 2. Obtener tasa de cambio inmediatamente
      try {
        const rate = await getExchangeRate(currency);
        setExchangeRate(rate);
        console.log(`💱 [Withdraw] Initial exchange rate for ${currency}:`, rate);
      } catch (error) {
        console.error('Error getting initial exchange rate:', error);
        setExchangeRate(1.0); // Fallback a 1.0 si falla
      }
    };

    initializeCurrency();
  }, [orderData]);
  
  // Actualizar tasa de cambio cada 30 segundos
  useEffect(() => {
    if (!exchangeRate) return; // Esperar inicialización

    const interval = setInterval(async () => {
      try {
        const rate = await getExchangeRate(displayCurrency);
        setExchangeRate(rate);
        console.log(`💱 [Withdraw] Exchange rate updated for ${displayCurrency}:`, rate);
      } catch (error) {
        console.error('Error updating exchange rate:', error);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [displayCurrency, exchangeRate])

  // Memoizar límites de monto desde la configuración del partner (igual que deposit)
  const minAmount = useMemo(() => {
    return orderData?.minimum_amount 
      ? Number.parseFloat(orderData.minimum_amount) 
      : 10; // Default minimum 10 USDT
  }, [orderData]);

  const maxAmount = useMemo(() => {
    return orderData?.maximum_amount 
      ? Number.parseFloat(orderData.maximum_amount) 
      : 10000; // Default maximum 10000 USDT
  }, [orderData]);
  
  // Hook para obtener límites y calcular comisiones de retiro (siempre en USDT)
  const withdrawAmount = selectedCurrency !== "USD" && amount && exchangeRate
    ? (Number.parseFloat(amount) / exchangeRate) 
    : (Number.parseFloat(amount) || 0)
  const { limits, currentFee, netAmount, totalDeducted, isLoading: isLoadingConfig } = useWithdrawalConfig(withdrawAmount)

  const handleEmailVerified = (email: string) => {
    setIsEmailVerified(true)
    setVerifiedEmail(email)
    setCurrentStep("amount")
  }

  const handleAmountSubmit = async () => {
    const currentWithdrawAmount = selectedCurrency !== "USD" && amount && exchangeRate
      ? (Number.parseFloat(amount) / exchangeRate) 
      : (Number.parseFloat(amount) || 0)
    
    // Validar con límites dinámicos
    if (!currentWithdrawAmount || currentWithdrawAmount < minAmount || currentWithdrawAmount > maxAmount || currentWithdrawAmount > userBalance) {
      console.log('⚠️ Invalid amount:', { currentWithdrawAmount, minAmount, maxAmount, userBalance });
      return;
    }

    setIsLoading(true)
    await new Promise((resolve) => setTimeout(resolve, 1000))
    setIsLoading(false)
    setCurrentStep("pin")
  }

  const handlePinSuccess = async (enteredPin: string, receivedSessionToken?: string) => {
    console.log('✅ [Withdraw] PIN verified, updating order status to pending_approval');
    console.log('🔑 [Withdraw] Session token received:', receivedSessionToken ? 'YES' : 'NO');
    
    setPin(enteredPin);
    setSessionToken(receivedSessionToken || null);
    setIsLoading(true);
    setCurrentStep("processing");
    setWithdrawalError(null);

    try {
      // Actualizar estado de la orden a pending_approval
      if (orderId) {
        console.log('📤 [Withdraw] Updating order status:', orderId);
        const response = await fetch('/api/partner/update-order-status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            order_id: orderId,
            status: 'pending_approval',
            amount: withdrawAmount,
            session_token: receivedSessionToken, // ✅ Incluir token de sesión para validación
          }),
        });

        const data = await response.json();
        
        if (!response.ok || !data.success) {
          console.error('❌ [Withdraw] Failed to update order status:', data);
          setWithdrawalError(data.error || 'Failed to process withdrawal request');
          setIsLoading(false);
          setCurrentStep("success"); // Mostrar error en la pantalla de status
          return;
        }
        
        console.log('✅ [Withdraw] Order status updated to pending_approval');
      }

      // Simular procesamiento
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      setIsLoading(false);
      setCurrentStep("success");
      
      // Iniciar countdown de redirección (5 segundos)
      setRedirectCountdown(5);
    } catch (error) {
      console.error('❌ [Withdraw] Error processing withdrawal:', error);
      setWithdrawalError(`Server error: ${(error as any).message || 'Unknown error'}`);
      setIsLoading(false);
      setCurrentStep("success"); // Mostrar error
    }
  }

  // Countdown de redirección
  useEffect(() => {
    if (redirectCountdown === null || redirectCountdown <= 0) return;

    const timer = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev === null || prev <= 1) {
          // Redirigir al callback URL o ejecutar onComplete
          if (orderData?.callback_url) {
            window.location.href = orderData.callback_url;
          } else {
            onComplete(Number.parseFloat(amount));
          }
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [redirectCountdown, orderData, amount, onComplete]);

  const handleBackNavigation = () => {
    if (currentStep === "email-verification") {
      onBack()
    } else if (currentStep === "amount") {
      onBack()  // Volver al email verification (ya no está en este componente)
    } else if (currentStep === "pin") {
      // Si prefilledAmount > 0, volver atrás (fuera del flow)
      // Si no, volver a amount selection
      if (prefilledAmount > 0) {
        onBack()
      } else {
        setCurrentStep("amount")
      }
    }
  }

  if (currentStep === "email-verification") {
    return <EmailVerificationScreen onEmailVerified={handleEmailVerified} onBack={onBack} mode="withdraw" />
  }

  const steps = [
    { key: "amount", icon: Wallet },
    { key: "pin", icon: Shield },
    { key: "processing", icon: Clock },
    { key: "success", icon: CheckCircle },
  ]

  const quickWithdraws = [
    { amount: "10", label: t('withdraw.quick_cash'), icon: "💸", popular: false },
    { amount: "25", label: t('withdraw.most_popular'), icon: "🔥", popular: true },
    { amount: "50", label: t('withdraw.standard'), icon: "💰", popular: false },
    { amount: "100", label: t('withdraw.high_amount'), icon: "💎", popular: false },
  ]

  return (
    <div className="max-w-md mx-auto min-h-screen bg-background">
      <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b border-border p-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={handleBackNavigation} disabled={isLoading}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-sm">N</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground tracking-tight">NBC TALK PAY</h1>
              <p className="text-xs text-muted-foreground font-medium">{t('withdraw.title')}</p>
            </div>
          </div>
          {isLoading && (
            <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
          )}
          <LanguageSelector />
        </div>

        {verifiedEmail && currentStep === "amount" && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg animate-in slide-in-from-top-2 duration-300">
            <p className="text-sm text-green-800">✓ {t('casino.verified_account')}: {verifiedEmail}</p>
          </div>
        )}
      </div>

      <div className="p-4 space-y-6">
        <div className="flex items-center justify-center gap-2">
          {steps.map((step, index) => (
            <div key={step.key} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-300 ${
                  currentStep === step.key
                    ? "bg-primary text-primary-foreground scale-110 shadow-lg"
                    : index < steps.findIndex((s) => s.key === currentStep)
                      ? "bg-green-100 text-green-800 shadow-md"
                      : "bg-muted text-muted-foreground"
                }`}
              >
                {index < steps.findIndex((s) => s.key === currentStep) ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <step.icon className="h-4 w-4" />
                )}
              </div>
              {index < 3 && (
                <div
                  className={`w-8 h-0.5 transition-all duration-500 ${
                    index < steps.findIndex((s) => s.key === currentStep) ? "bg-green-200" : "bg-muted"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Amount Step */}
        {currentStep === "amount" && (
          <div className="animate-in slide-in-from-right-4 duration-300 space-y-4">
            {/* NBC TALK PAY Account Verified */}
            {nbcUser && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                      <span className="text-sm font-semibold text-green-800">{t('casino.nbc_account_verified')}</span>
                    </div>
                    
                    {nbcUser.user_data?.nbc_talk_id && (
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary" className="text-xs">
                          ID: {nbcUser.user_data.nbc_talk_id}
                        </Badge>
                      </div>
                    )}
                    
                    {(nbcUser.wallet_address || nbcUser.public_key) && (
                      <div className="flex items-center gap-2 text-xs">
                        <Wallet className="h-3 w-3" />
                        <code className="bg-white px-2 py-1 rounded text-xs border">
                          {(() => {
                            const address = nbcUser.wallet_address || nbcUser.public_key || '';
                            return `${address.substring(0, 8)}...${address.substring(address.length - 6)}`;
                          })()}
                        </code>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Balance Disponible */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-blue-900">{t('withdraw.available_balance')}</div>
                  <div className="text-xs text-blue-600">Your NBC TALK PAY wallet</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-900">{userBalance.toFixed(2)} USDT</div>
                  {displayCurrency !== "USD" && exchangeRate && (
                    <div className="text-xs text-blue-600">≈ {getCurrencySymbol(displayCurrency)}{(userBalance * exchangeRate).toFixed(2)} {displayCurrency}</div>
                  )}
                </div>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowUp className="h-5 w-5" />
                  {t('withdraw.enter_amount_title')}
                </CardTitle>
                <CardDescription>
                  {t('withdraw.minimum_withdrawal')}: ${minAmount} USDT{displayCurrency !== "USD" && exchangeRate && ` (≈ ${getCurrencySymbol(displayCurrency)}${(minAmount * exchangeRate).toFixed(2)})`} • {t('withdraw.maximum_withdrawal')}: ${maxAmount} USDT
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="amount">{t('withdraw.amount_label')}</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        id="amount"
                        type="number"
                        placeholder="10.00"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        max={userBalance}
                        className={`text-lg transition-all duration-200 focus:scale-[1.02] ${prefilledAmount > 0 ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        disabled={isLoading || prefilledAmount > 0}
                        readOnly={prefilledAmount > 0}
                      />
                    </div>
                    <Select 
                      value={selectedCurrency} 
                      onValueChange={(value: string) => setSelectedCurrency(value)}
                      disabled={prefilledAmount > 0}
                    >
                      <SelectTrigger className={`w-24 ${prefilledAmount > 0 ? 'bg-gray-100 cursor-not-allowed' : ''}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USDT</SelectItem>
                        <SelectItem value={displayCurrency}>{displayCurrency}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Mostrar conversión si no es USD */}
                  {amount && withdrawAmount > 0 && selectedCurrency !== "USD" && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <TrendingDown className="h-3 w-3" />
                      <span>{t('casino.equivalent_amount')}: {exchangeRate ? (Number.parseFloat(amount) / exchangeRate).toFixed(2) : '...'} USDT</span>
                      {exchangeRate && <span className="text-xs opacity-70">(Rate: {exchangeRate.toFixed(4)})</span>}
                    </div>
                  )}
                  {amount && withdrawAmount > 0 && selectedCurrency === "USD" && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <TrendingDown className="h-3 w-3" />
                      <span>≈ {getCurrencySymbol(displayCurrency)}{exchangeRate ? (withdrawAmount * exchangeRate).toFixed(2) : '...'} {displayCurrency}</span>
                      {exchangeRate && <span className="text-xs opacity-70">(Rate: {exchangeRate.toFixed(4)})</span>}
                    </div>
                  )}
                  
                  {/* Desglose de comisiones - Estilo deposit */}
                  {amount && withdrawAmount > 0 && !isLoadingConfig && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                      <div className="text-sm font-medium text-blue-800">{t('casino.payment_details')}</div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-blue-600">{t('withdraw.withdraw_amount')}:</span>
                          <span className="font-mono text-blue-900">{withdrawAmount.toFixed(2)} USDT</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-600">{t('withdraw.commission_fee')}:</span>
                          <span className="font-mono text-orange-600">{currentFee.toFixed(2)} USDT</span>
                        </div>
                        <div className="border-t border-blue-300 pt-1 mt-1 flex justify-between">
                          <span className="font-semibold text-blue-800">{t('withdraw.you_will_receive')}:</span>
                          <span className="font-mono font-bold text-green-700">{netAmount.toFixed(2)} USDT</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Validaciones de monto */}
                  {amount && withdrawAmount > 0 && (
                    <>
                      {withdrawAmount < minAmount && (
                        <Alert variant="destructive" className="mt-2">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>
                            {t('withdraw.minimum_withdrawal')}: ${minAmount} USDT{exchangeRate && ` (≈ ${getCurrencySymbol(displayCurrency)}${(minAmount * exchangeRate).toFixed(2)} ${displayCurrency})`}
                          </AlertDescription>
                        </Alert>
                      )}
                      {withdrawAmount > maxAmount && (
                        <Alert variant="destructive" className="mt-2">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>
                            {t('withdraw.maximum_withdrawal')}: ${maxAmount} USDT{exchangeRate && ` (≈ ${getCurrencySymbol(displayCurrency)}${(maxAmount * exchangeRate).toFixed(2)} ${displayCurrency})`}
                          </AlertDescription>
                        </Alert>
                      )}
                      {withdrawAmount > userBalance && (
                        <Alert variant="destructive" className="mt-2">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>
                            {t('withdraw.insufficient_balance')}
                          </AlertDescription>
                        </Alert>
                      )}
                    </>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-purple-500" />
                    <span className="text-sm font-medium text-muted-foreground">{t('withdraw.quick_withdrawals')}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {quickWithdraws.map((preset) => (
                      <Button
                        key={preset.amount}
                        variant={preset.popular ? "default" : "outline"}
                        size="sm"
                        onClick={() => setAmount(preset.amount)}
                        disabled={isLoading || Number.parseFloat(preset.amount) > userBalance}
                        className={`transition-all duration-200 hover:scale-105 flex flex-col gap-1 h-auto py-3 ${
                          preset.popular ? "ring-2 ring-purple-200 bg-linear-to-r from-purple-600 to-purple-700" : ""
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          <span className="text-lg">{preset.icon}</span>
                          <span className="font-bold">${preset.amount}</span>
                        </div>
                        <span className="text-xs opacity-80">{preset.label}</span>
                      </Button>
                    ))}
                  </div>
                </div>

                <Button
                  className="w-full transition-all duration-200 hover:scale-[1.02]"
                  onClick={handleAmountSubmit}
                  disabled={
                    !amount || 
                    withdrawAmount <= 0 ||
                    withdrawAmount < minAmount || 
                    withdrawAmount > maxAmount || 
                    withdrawAmount > userBalance || 
                    isLoading ||
                    isLoadingConfig
                  }
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('withdraw.processing_button')}
                    </div>
                  ) : (
                    t('withdraw.continue')
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* PIN Step - Using CasinoPinConfirmation with full transaction details */}
        {currentStep === "pin" && (
          <div className="animate-in slide-in-from-right-4 duration-300">
            <CasinoPinConfirmation
              transactionType="casino_withdrawal"
              transactionDetails={{
                balance: userBalance.toFixed(2),
                amount: `${withdrawAmount.toFixed(2)} USDT`,
                fee: `${currentFee.toFixed(2)} USDT`,
                total: `${netAmount.toFixed(2)} USDT`,
                recipient: nbcUser?.wallet_address || nbcUser?.public_key || verifiedEmail,
                method: t('withdraw.title'),
                error: '',
              }}
              email={verifiedEmail}
              onSuccess={handlePinSuccess}
              onCancel={() => setCurrentStep("amount")}
              onMaxAttemptsReached={() => {
                console.log('🚫 Max PIN attempts reached in withdrawal');
                setCurrentStep("amount");
              }}
            />
          </div>
        )}

        {/* Processing Step */}
        {currentStep === "processing" && (
          <div className="animate-in slide-in-from-right-4 duration-300">
            <Card>
              <CardContent className="pt-6 pb-6">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
                  <div>
                    <h3 className="text-xl font-bold">Processing Withdrawal</h3>
                    <p className="text-muted-foreground mt-2">Please wait while we process your withdrawal...</p>
                  </div>
                  <div className="bg-muted rounded-lg p-4">
                    <div className="text-2xl font-bold text-purple-600">${Number.parseFloat(amount).toFixed(2)}</div>
                    <div className="text-sm text-muted-foreground">Being sent to your NBC TALK PAY wallet</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Success Step */}
        {currentStep === "success" && (
          <div className="animate-in slide-in-from-right-4 duration-300">
            <Card>
              <CardHeader className="text-center">
                {withdrawalError ? (
                  <>
                    <div className="mx-auto my-4 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
                      <AlertTriangle className="h-10 w-10 text-red-600" />
                    </div>
                    <CardTitle className="text-2xl text-red-600">{t('withdraw.request_failed')}</CardTitle>
                    <CardDescription>
                      {withdrawalError}
                    </CardDescription>
                  </>
                ) : isLoading ? (
                  <>
                    <div className="mx-auto my-4 flex h-20 w-20 items-center justify-center">
                      <div className="h-16 w-16 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                    </div>
                    <CardTitle className="text-2xl">{t('common.loading')}</CardTitle>
                    <CardDescription>
                      {t('withdraw.processing_request')}
                    </CardDescription>
                  </>
                ) : (
                  <>
                    <div className="mx-auto my-4 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                      <CheckCircle className="h-10 w-10 text-green-600" />
                    </div>
                    <CardTitle className="text-2xl text-green-600">{t('withdraw.request_successful')}</CardTitle>
                    <CardDescription>
                      {t('withdraw.request_description', { amount: `${withdrawAmount.toFixed(2)} USDT` })}
                    </CardDescription>
                    {redirectCountdown !== null && redirectCountdown > 0 && (
                      <CardDescription className="text-sm text-muted-foreground mt-2">
                        {t('withdraw.redirecting_in', { seconds: redirectCountdown })}
                      </CardDescription>
                    )}
                  </>
                )}
              </CardHeader>

              <CardContent className="space-y-4">
                {!withdrawalError && !isLoading && (
                  <div className="p-4 bg-muted rounded-lg space-y-2">
                    <div className="text-sm font-medium">{t('withdraw.request_details')}</div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('withdraw.order_id')}:</span>
                      <span className="font-mono text-xs">{orderData?.partner_order_id || orderId}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('withdraw.amount')}:</span>
                      <span>{withdrawAmount.toFixed(2)} USDT</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('withdraw.status')}:</span>
                      <span className="text-yellow-600 font-medium">{t('withdraw.pending_approval')}</span>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  {withdrawalError ? (
                    <Button 
                      className="w-full"
                      onClick={() => {
                        setCurrentStep("amount");
                        setWithdrawalError(null);
                      }}
                    >
                      {t('withdraw.try_again')}
                    </Button>
                  ) : !isLoading ? (
                    <>
                      {/* Botón "Make Another Withdrawal" oculto - no usar en flujo partner */}
                      {/* <Button 
                        className="w-full"
                        onClick={() => {
                          if (orderData?.callback_url) {
                            window.location.href = orderData.callback_url;
                          } else {
                            setCurrentStep("amount");
                            setAmount("");
                            setWithdrawalError(null);
                            setRedirectCountdown(null);
                            onComplete?.(withdrawAmount);
                          }
                        }}
                      >
                        {t('withdraw.make_another_withdrawal')}
                      </Button> */}
                      
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => {
                          if (orderData?.callback_url) {
                            window.location.href = orderData.callback_url;
                          } else if (onBack) {
                            onBack();
                          }
                        }}
                      >
                        {t('withdraw.back_to_partner')}
                      </Button>
                    </>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <div className="p-4 border-t border-border bg-muted/30">
        <div className="text-center">
          <p className="text-xs text-muted-foreground/70">
            © 2024 Powered by <span className="font-semibold text-muted-foreground">NBC BANQ</span>
          </p>
        </div>
      </div>
    </div>
  )
}
